// https://www.intercom.com/help/en/articles/170-integrate-intercom-in-a-single-page-app

import React, { createContext, useContext, useEffect, ReactNode } from 'react';
import isUndefined from 'lodash/isUndefined';
import { useProfile } from '@/contexts/profile';

export const Intercom = __CLIENT__ ? window?.Intercom : null;

type IntercomState = {
  intercomActive: boolean;
};

export const IntercomContext = createContext<IntercomState>({
  intercomActive: false,
});

type Data = Record<string, unknown | null | number | string>;

const transformNulls = (o: Data) => {
  const res: Data = {};
  Object.keys(o).forEach((key) => {
    res[key] = o[key] ?? '';
  });
  return res;
};

export const update = (data: Data = {}): void => {
  if (Intercom) {
    Intercom('update', {
      ...transformNulls(data),
      app_id: INTERCOM_APP_ID,
      updated_at: Math.floor(new Date().getTime() / 1000),
    });
  }
};

export const trackEvent = (name: string, metadata = {}): void => {
  if (Intercom) {
    Intercom('trackEvent', name, transformNulls(metadata));
  }
};

export const shutdown = (): void => {
  if (Intercom) {
    Intercom('shutdown');
  }
};

export const IntercomProvider = ({
  children,
}: {
  children: ReactNode;
}): ReactNode => {
  const { data } = useProfile() || {};
  const user = data?.profile ?? {};
  const {
    permissions,
    emailAddress,
    primaryIdentityId,
    familyName,
    givenName,
  } = user;
  const name = [givenName, familyName].filter(Boolean).join(' ');
  const organiserIds = (permissions?.organiserPermissions ?? [])
    .map((p) => p?.id)
    .filter(Boolean);
  const isOrganiser = !!organiserIds.length;
  const intercomActive = isOrganiser;

  useEffect(() => {
    update({
      hide_default_launcher: !intercomActive,
    });
    if (intercomActive) {
      update({
        email: emailAddress,
        user_id: primaryIdentityId,
        isOrganiser,
        organiserIds: organiserIds.join(','),
        name,
        environment: LDT_ENV,
        isProduction: LDT_ENV === 'production',
      });
    }
  }, [intercomActive]);

  return (
    <IntercomContext.Provider value={{ intercomActive }}>
      {children}
    </IntercomContext.Provider>
  );
};

interface IntercomReturn extends IntercomState {
  update: typeof update;
  trackEvent: typeof trackEvent;
}

export const useIntercom = (): IntercomReturn => {
  const intercom = useContext(IntercomContext);

  if (isUndefined(intercom)) {
    throw new Error('useIntercom must be used within the IntercomProvider');
  }

  return {
    ...intercom,
    update: intercom.intercomActive ? update : () => {},
    trackEvent: intercom.intercomActive ? trackEvent : () => {},
  };
};
